import { Field } from "../../components/Field";
import { Modal } from "../../components/Modal";
import { ModalCloseButton } from "../../components/ModalCloseButton";

interface TemplateReplaceEditorProps {
  templateId: string;
  /** Looked up by the caller from the already-fetched list — may be briefly `undefined` while that list is still loading. */
  templateName: string | undefined;
  /** The template's CURRENT active version, looked up the same way as `templateName` — drives the "N+1" wording (REDISENO-PANEL.md §8.1). */
  currentVersion: number | undefined;
  html: string;
  error: string | null;
  pending: boolean;
  onHtmlChange: (html: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onCancel: () => void;
}

/**
 * Replace-content editor (redesign-panel-hubspot-mailgun, PR 15 slice C,
 * task 15.1/15.2/15.3). All state (`html`/`error`/`pending`) stays owned by
 * `TemplatesScreen` — this component is a plain controlled panel, same
 * convention `AttachmentsPanel` uses — because `TemplatesScreen` also needs
 * the draft's dirtiness for `useUnsavedChangesBlocker`'s "Ver detalle" guard,
 * which lives outside this component entirely.
 *
 * Same `replaceTemplateContent` call and typed-Liquid-preservation-on-error
 * contract as before (never resets on failure, only on success/cancel) —
 * with real pending/disabled state added (task 15.3 "duplicate
 * submission"). A `409` response is surfaced VERBATIM via `error` — the
 * server already names the blocking campaign(s) in that message (e.g.
 * "plantilla referenciada por campana(s) programada(s)/enviando: Campana
 * Black Friday", task 15.1 "named blocking campaigns") — this component
 * never re-derives or rewrites that text.
 *
 * User follow-up 2026-09-18: converted from a full-width inline sibling
 * panel (finding #10's scroll-into-view no longer applies — a centered
 * popup is always in view) to the shared `Modal` popup, matching
 * `TemplateAttachmentsPanel`'s own conversion in the same request.
 */
export function TemplateReplaceEditor({
  templateId,
  templateName,
  currentVersion,
  html,
  error,
  pending,
  onHtmlChange,
  onSubmit,
  onCancel,
}: TemplateReplaceEditorProps) {
  return (
    <Modal ariaLabel="Reemplazar contenido de plantilla" onClose={onCancel}>
      <form
        onSubmit={onSubmit}
        aria-label="Reemplazar contenido de plantilla"
        className="v2-template-replace-editor"
      >
        <header className="v2-modal__header">
          <h2>Reemplazar contenido{templateName ? ` — ${templateName}` : ""}</h2>
          <ModalCloseButton onClose={onCancel} disabled={pending} />
        </header>

        <Field label="Nuevo HTML" htmlFor={`replace-html-${templateId}`}>
          <textarea
            id={`replace-html-${templateId}`}
            className="v2-u-target-min"
            value={html}
            onChange={(e) => onHtmlChange(e.target.value)}
            disabled={pending}
            required
          />
        </Field>

        {/* REDISENO-PANEL.md §8.1 literal copy: "Se creará la versión N+1. Las
            campañas ya enviadas conservan su versión." — persistent
            explanatory copy, distinct from the dynamic `error` alert below
            (which only renders once a failure actually happens). */}
        <p className="v2-template-replace-editor__note">
          {currentVersion !== undefined
            ? `Se creará la versión ${currentVersion + 1}. Las campañas ya enviadas conservan su versión.`
            : "Se creará la siguiente versión. Las campañas ya enviadas conservan su versión."}
        </p>

        {error && <p role="alert">{error}</p>}

        <div className="v2-template-replace-editor__footer">
          <button type="submit" className="v2-u-target-min" disabled={pending} aria-busy={pending}>
            {pending ? "Guardando…" : "Guardar contenido"}
          </button>
          <button type="button" className="v2-u-target-min" onClick={onCancel} disabled={pending}>
            Cancelar
          </button>
        </div>
      </form>
    </Modal>
  );
}
